import { KeyboardEventKey } from './KeyboardEventKeys';

export type KeyActionPair = {
  sceneContext?: string;
  keyCodes: KeyboardEventKey[];
  pressAction?: { action: () => void; fireOnce?: boolean };
  releaseAction?: () => void;
};

/**
 * Represents a keyboard input handler that allows binding keys to actions and handling keyboard input.
 */
export class KeyboardInputHandler {
  private static _keyActionMap: Map<KeyboardEventKey[], { sceneContext?: string; press?: { action: () => void; fireOnce?: boolean }; release?: () => void }>;
  private static _keyState: Map<string, boolean>;
  private static _prevKeyState: Map<string, boolean>;
  private static _keyBindCheckSet: Set<string>;
  private static _keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  private static _keyupHandler: ((event: KeyboardEvent) => void) | null = null;

  /**
   * Initializes the KeyboardInputHandler.
   *
   * @param keybindings - Optional keybindings to be applied.
   */
  public static initialize(keybindings?: KeyActionPair[]): void {
    this._keyActionMap = new Map();
    this._keyState = new Map();
    this._prevKeyState = new Map();
    this._keyBindCheckSet = new Set();

    if (keybindings) {
      this.bindKeys(keybindings);
    }

    // Store handler references so they can be properly removed later
    this._keydownHandler = (event: KeyboardEvent) => {
      this._keyState.set(event.key, true);

      if (this._keyBindCheckSet.has([event.key].toString())) {
        event.preventDefault();
      }
    };

    this._keyupHandler = (event: KeyboardEvent) => {
      this._keyState.set(event.key, false);

      if (this._keyBindCheckSet.has([event.key].toString())) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', this._keydownHandler);
    window.addEventListener('keyup', this._keyupHandler);
  }

  /**
   * Binds multiple key-action pairs to the keyboard input handler.
   *
   * @param keyActionPairs - An array of key-action pairs.
   */
  public static bindKeys(keyActionPairs: KeyActionPair[]): void {
    for (const pair of keyActionPairs) {
      this.bindKey(pair);
    }
  }

  /**
   * Binds a combination of keys to the specified press and release actions.
   * Example:
   *
   *  keys: ['Control', 'c'],
   *  pressAction: () => console.log('The "Control+C" key combination was pressed'),
   *  releaseAction: () => console.log('The "Control+C" key combination was released')
   *
   * @param keys - An array of keys to bind.
   * @param pressAction - The action to perform when any of the keys are pressed.
   * @param releaseAction - The action to perform when any of the keys are released.
   */
  public static bindKey(keyActionPair: KeyActionPair): void {
    if (!keyActionPair.pressAction && !keyActionPair.releaseAction) {
      throw new Error('At least one of pressAction or releaseAction must be provided');
    }
    this._keyActionMap.set(
      keyActionPair.keyCodes,
      { sceneContext: keyActionPair.sceneContext, press: keyActionPair.pressAction, release: keyActionPair.releaseAction }
    );
    this._keyBindCheckSet.add(keyActionPair.keyCodes.toString());
  }

  /**
   * Removes the key bindings for the specified keys.
   *
   * @param keys - An array of keys to unbind.
   */
  public static unbindKey(keys: KeyboardEventKey[]): void {
    this._keyActionMap.delete(keys);
    this._keyBindCheckSet.delete(keys.toString());
  }

  public static clearAllBinds(): void {
    this._keyActionMap.clear();
    this._keyBindCheckSet.clear();
  }

  public static clearAllBindsForContext(sceneContext: string): void {
    for (const [keys, actions] of this._keyActionMap) {
      if (actions.sceneContext === sceneContext) {
        this._keyActionMap.delete(keys);
        this._keyBindCheckSet.delete(keys.toString());
      }
    }
  }

  /**
   * Checks if a specific key is currently pressed.
   *
   * @param key - The key to check.
   * @returns True if the key is pressed, false otherwise.
   */
  public static isKeyPressed(key: KeyboardEventKey): boolean {
    return this._keyState.get(key) || false;
  }

  /**
   * Handles the keyboard input by checking the state of registered keys and invoking the corresponding actions.
   */
  public static handleInput(currentScene: string): void {
    for (const [keys, actions] of this._keyActionMap) {
      if (!actions.sceneContext || actions.sceneContext === currentScene) {
        if (keys.every((key) => this._keyState.get(key) && (!actions.press?.fireOnce || !this._prevKeyState.get(key))) && actions.press?.action) {
          actions.press.action();
        }
        else if (keys.some((key) => !this._keyState.get(key) && this._prevKeyState.get(key)) && actions.release) {
          actions.release();
        }
      }
    }

    // After handling input, set the current state as the previous state for the next cycle
    this._prevKeyState = new Map(this._keyState);
  }

  /**
   * Destroys the KeyboardInputHandler and cleans up all event listeners and static state.
   */
  public static destroy(): void {
    // Remove event listeners
    if (this._keydownHandler) {
      window.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }

    if (this._keyupHandler) {
      window.removeEventListener('keyup', this._keyupHandler);
      this._keyupHandler = null;
    }

    // Clear all static state
    this._keyActionMap?.clear();
    this._keyState?.clear();
    this._prevKeyState?.clear();
    this._keyBindCheckSet?.clear();

    // Reset to undefined
    this._keyActionMap = undefined as never;
    this._keyState = undefined as never;
    this._prevKeyState = undefined as never;
    this._keyBindCheckSet = undefined as never;
  }
}
